import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import {
  type MeResponse,
  membresiaEnObra,
  meQueryOptions,
  type ObraMembresia,
  ROL_ADMIN_OBRA,
  ROL_CUALIFICADO,
  type RolObra,
} from "@/lib/auth";

/**
 * ensureAuth garantiza una sesión válida resolviendo /me desde la caché de Query
 * (sin round-trip si ya está cacheada). Un 401 redirige a /login conservando el
 * deep-link solicitado en el search param `redirect`.
 *
 * F4 (offline): la redirección por sesión caducada NO debe descartar la outbox de
 * mutaciones pendientes; el deep-link se preserva para retomar el trabajo al volver.
 */
export async function ensureAuth(queryClient: QueryClient, href: string): Promise<MeResponse> {
  try {
    return await queryClient.ensureQueryData(meQueryOptions);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      throw redirect({ to: "/login", search: { redirect: href } });
    }
    throw err;
  }
}

/**
 * ensureOnboarded aplica el orden de onboarding obligatorio: PRIMERO el cambio de
 * contraseña, DESPUÉS la aceptación del NDA. Coincide con los gates del backend
 * (RequirePasswordChanged → RequireNDA). Lanza un redirect si falta algún paso.
 */
export function ensureOnboarded(me: MeResponse): void {
  if (me.debe_cambiar_password) {
    throw redirect({ to: "/mi-clave" });
  }
  if (me.nda_pendiente) {
    throw redirect({ to: "/nda" });
  }
}

/**
 * esGestorDeObra es la regla común del backend `people.EsRolGestorObra` más el admin
 * global: admin de instancia, ADMIN_OBRA o CUALIFICADO EN esa obra. La exponen dos guards
 * con nombres distintos porque responden a dos preguntas distintas (gestionar la fábrica
 * vs. actuar sobre trabajo ajeno); se define una sola vez para que no puedan divergir.
 */
function esGestorDeObra(me: MeResponse, obraId: number): boolean {
  if (me.es_admin) {
    return true;
  }
  const rol = membresiaEnObra(me, obraId)?.rol;
  return rol === ROL_ADMIN_OBRA || rol === ROL_CUALIFICADO;
}

/**
 * canManageFactory refleja el gate de mutación de los maestros de fábrica (F2, #171):
 * el admin global siempre, o quien sea ADMIN_OBRA/CUALIFICADO EN esa obra (espejo de
 * RequireFactoryManager del backend). La UI oculta las acciones de escritura a quien no
 * lo cumple; el backend es el guardián real.
 */
export function canManageFactory(me: MeResponse, obraId: number): boolean {
  return esGestorDeObra(me, obraId);
}

/**
 * puedeOverrideEnObra es el espejo del override de PROPIEDAD del backend
 * (`loto/handler.go::puedeOverrideEnObra`): quién puede retirar un candado que no colocó,
 * cerrar un LOTO que no abrió o cambiarle el procedimiento — el admin global, el
 * ADMIN_OBRA o el CUALIFICADO de esa obra. Lo consume la ficha del LOTO (#255 D11) para
 * mostrar esas acciones DESHABILITADAS CON MOTIVO a quien no las tiene, en vez de dejarle
 * chocar contra el 403; el servidor sigue siendo el guardián real
 * (CIERRE_NO_PERMITIDO / RETIRO_NO_PERMITIDO / CAMBIO_PROC_NO_PERMITIDO).
 */
export function puedeOverrideEnObra(me: MeResponse, obraId: number): boolean {
  return esGestorDeObra(me, obraId);
}

/**
 * puedeRegistrarEnObra lee la regla puede_registrar que el backend calcula por obra
 * (admin/ADMIN_OBRA/CUALIFICADO siempre; AUTORIZADO con autorización LOTO vigente). Se
 * limita a leer obras[].puede_registrar del /me; el backend es el guardián real.
 */
export function puedeRegistrarEnObra(me: MeResponse, obraId: number): boolean {
  return membresiaEnObra(me, obraId)?.puede_registrar ?? false;
}

/**
 * requireObraMember exige ser miembro de la obra (o admin global) para montar el árbol
 * de dominio de una obra. Sin pertenencia redirige al selector de obras (el backend
 * responde 404 a no-miembros, incluido el deep-link a una obra ajena).
 */
export function requireObraMember(me: MeResponse, obraId: number): void {
  if (!me.es_admin && membresiaEnObra(me, obraId) === undefined) {
    throw redirect({ to: "/obras" });
  }
}

/**
 * ObraActiva es el contexto de obra que el `beforeLoad` de `obraLayoutRoute` (#157)
 * deja para las páginas hijas: el id numérico ya validado, la membresía del usuario en
 * ella (undefined si es admin global no-miembro) y el rol efectivo en la obra (el admin
 * global opera como "ADMIN").
 */
export type ObraActiva = {
  obraId: number;
  obra: ObraMembresia | undefined;
  rolEnObra: RolObra;
};

/**
 * resolverObraActiva valida el `$obraId` de la ruta contra el `me` (rehidratado por
 * RouterGate, así que funciona igual online y offline) y devuelve el contexto de obra.
 * Redirige al selector `/obras` si el id no es un entero positivo o si el usuario no es
 * miembro de la obra (ni admin global): es el guardián del deep-link a una obra ajena.
 */
export function resolverObraActiva(me: MeResponse, obraIdParam: string | undefined): ObraActiva {
  const obraId = Number(obraIdParam);
  if (!Number.isInteger(obraId) || obraId <= 0) {
    throw redirect({ to: "/obras" });
  }
  requireObraMember(me, obraId);
  const obra = membresiaEnObra(me, obraId);
  // El admin global no listado como miembro opera con rol efectivo "ADMIN".
  const rolEnObra: RolObra = obra?.rol ?? "ADMIN";
  return { obraId, obra, rolEnObra };
}

/** requireAdmin monta rutas solo del administrador global (gestión de usuarios, obras). */
export function requireAdmin(me: MeResponse): void {
  if (!me.es_admin) {
    throw redirect({ to: "/" });
  }
}
